import Stack from "@mui/material/Stack/Stack";
import React, { useEffect } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import QrCodeIcon from "@mui/icons-material/QrCode";
import DrawerAppBar from "../../../../../components/mobile/Navbar";
import { useNavigate } from "react-router-dom";
import { LoadPositionList } from "../../../../../../domain/usecases/load-position-list";
import { PositionNotFountError } from "../../../../../../domain/errors/position-not-found-error";
import { Position } from "../../../../../../domain/models/position-model";
import { useLocation } from "react-router-dom";
import { FinishTransfer } from "../../../../../../domain/usecases/finish-transfer";
import { NormalizedCargoItem } from "../../../../../../domain/models/cargo-model";
import { LoadTransfer } from "../../../../../../domain/usecases/load-transfer";
import { LoadProductList } from "../../../../../../domain/usecases/load-product-list";
import { Product } from "../../../../../../domain/models/product-model";
import { DeleteCargo } from "../../../../../../domain/usecases/delete-cargo";
import { CustomDialog } from "../../../../../components/Dialog";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store";

type Props = {
  loadPositionList: LoadPositionList;
  loadProductList: LoadProductList;
  finishTransfer: FinishTransfer;
  loadTransfer: LoadTransfer;
  deleteCargo: DeleteCargo;
};

export const TransferDestinationCollector: React.FC<Props> = ({
  loadPositionList,
  loadProductList,
  finishTransfer,
  loadTransfer,
  deleteCargo,
}) => {
  const getCurrentWarehouse = useSelector<
    RootState,
    () => { id: number; name: string }
  >((state) => state.getCurrentWarehouse);
  const navigate = useNavigate();
  const location = useLocation();
  const [transfer, setTransfer] = React.useState<NormalizedCargoItem | null>();
  const [code, setCode] = React.useState<string>("");
  const [position, setPosition] = React.useState<Position | null>(null);
  const [product, setProduct] = React.useState<Product | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] =
    React.useState<boolean>(false);

  useEffect(() => {
    async function fetch() {
      try {
        let objTransfer: NormalizedCargoItem | undefined = undefined;

        if (location.state && location.state.transfer) {
          setTransfer(location.state.transfer[0]);
          objTransfer = location.state.transfer[0];
        } else {
          const fetchTransfer = await loadTransfer.load();
          if (fetchTransfer.length === 0) return navigate("/mobile/tasks");

          if (fetchTransfer.length > 1) return alert("Erro crítico");
          if (fetchTransfer.length === 1) {
            setTransfer(fetchTransfer[0]);
            objTransfer = fetchTransfer[0];
          }
        }

        const product = await loadProductList.load({
          code: objTransfer!.eanCode,
        });
        setProduct(product[0]);
      } catch (error) {
        console.log(error);
      }
    }
    fetch();
  }, []);

  const onBlur = async () => {
    if (!code) return;
    try {
      const warehouse = getCurrentWarehouse();
      setIsLoading(true);
      const list = await loadPositionList.load({
        warehouseId: warehouse.id,
        filterOptions: { code },
      });
      if (!list || list.length === 0) {
        return alert("Posição não encontada");
      }
      setPosition(list[0]);
    } catch (error: any) {
      if (error instanceof PositionNotFountError) {
        alert(error.message);
      } else {
        alert("Um erro inesperado ocorreu. Tente novamente.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = async () => {
    if (!position) {
      return alert("Insira o código da posição antes de continuar.");
    }
    if (!transfer) return alert("Erro");

    const reservationIds: number[] = transfer.inventoryReservations?.map(
      (reservation) => reservation.id
    ) as number[];
    try {
      const res = await finishTransfer.finish({
        reservationIds,
        destinationId: position.id!,
      });
      if (!res) return alert("Erro. Tente novamente");
      navigate("/mobile/tasks");
    } catch (error) {
      alert("Um erro inesperado aconteceu. Tente novamente");
    }
  };

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      const deleted = await deleteCargo.delete(transfer!.cargoId!);

      if (deleted) {
        alert("Transferência deletada com sucesso!");
        return navigate("/mobile/tasks");
      }
    } catch (error) {
      alert("Não foi possível deletar a tranferência");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <CustomDialog
        isOpen={isCancelDialogOpen}
        title="Tem certeza que deseja cancelar a transferência?"
        content=""
        onYes={() => {
          handleDelete();
          setIsCancelDialogOpen(false);
        }}
        yesText="Sim"
        noText="Não"
        onNo={() => {
          setIsCancelDialogOpen(false);
        }}
        handleClose={() => {
          setIsCancelDialogOpen(false);
        }}
      />
      <DrawerAppBar
        title="Finalizar transferência"
        onBack={() => {
          navigate("/mobile/tasks");
        }}
      />
      <Stack sx={{ m: "20px" }} spacing={4}>
        {
          <Paper sx={{ padding: "20px" }}>
            <Stack spacing={2}>
              <Typography fontWeight="bold" fontSize="24px">
                {product?.description}
              </Typography>
              <Typography>{`Quantidade: ${
                (transfer?.inventoryReservations?.reduce((acc, reservation) => {
                  acc += reservation.amount;
                  return acc;
                }, 0) || 0) / (product?.packageAmount || 1)
              }`}</Typography>
            </Stack>
          </Paper>
        }
        <Paper>
          <Stack spacing={4} sx={{ padding: "20px" }}>
            {position && (
              <Typography fontWeight="bold" fontSize={"26px"}>
                {" "}
                {`${position?.street}.${position?.building}.${position?.level}.${position?.apartment}`}
              </Typography>
            )}
            <Stack direction="row">
              <TextField
                id="outlined-basic"
                label="Código da Posição"
                variant="outlined"
                sx={{
                  mx: "5px",
                }}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                onBlur={onBlur}
              />
              <Button
                variant="outlined"
                color="inherit"
                sx={{
                  mx: "5px",
                }}
              >
                <QrCodeIcon />
              </Button>
            </Stack>
          </Stack>
        </Paper>
        <Button
          variant="contained"
          color="error"
          sx={{ color: "white", height: "50px", fontWeight: "bold" }}
          onClick={() => {
            setIsCancelDialogOpen(true);
          }}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          color="secondary"
          sx={{ color: "white", height: "50px", fontWeight: "bold" }}
          onClick={handleClick}
        >
          Confirmar
        </Button>
      </Stack>
    </>
  );
};
